import React, { useState } from "react";
import { ArrowLeft, Save, Loader2, Copy, FileDown, ClipboardPaste } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/Cards";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import DishLibrary from "@/components/menus/DishLibrary";
import MenuEmailDialog from "@/components/menus/MenuEmailDialog";
import { Mail } from "lucide-react";

const MEALS = [["Öğle Yemeği", "Öğle Yemeği Menüsü"]];
const GUN = ["Paz", "Pzt", "Sal", "Çar", "Per", "Cum", "Cmt"];
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const monthDays = (m) => { const [y, mo] = m.split("-").map(Number); const n = new Date(y, mo, 0).getDate(); return Array.from({ length: n }, (_, i) => `${m}-${String(i + 1).padStart(2, "0")}`); };

export default function MenuEditor({ menu, customers, onClose }) {
  const [form, setForm] = useState({ ...menu, days: menu.days || {}, customer_ids: menu.customer_ids || [] });
  const [saving, setSaving] = useState(false);
  const [clip, setClip] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [activeCell, setActiveCell] = useState(null); // {ds, idx}
  const [emailOpen, setEmailOpen] = useState(false);
  const MEAL = MEALS[0][0];
  const getSlots = (ds) => { const d = form.days[ds] || {}; if (Array.isArray(d.slots)) return [...d.slots, ...Array(7).fill("")].slice(0, 7); const parts = (d[MEAL] || "").split(",").map((s) => s.trim()).filter(Boolean); return [...parts, ...Array(7).fill("")].slice(0, 7); };
  const setSlot = (ds, idx, v) => { const slots = getSlots(ds); slots[idx] = v; setForm((f) => ({ ...f, days: { ...f.days, [ds]: { slots, [MEAL]: slots.filter((s) => s.trim()).join(", ") } } })); setDirty(true); };
  const pickDish = (name) => { if (!activeCell) return; setSlot(activeCell.ds, activeCell.idx, name); const next = activeCell.idx < 6 ? activeCell.idx + 1 : 6; setActiveCell({ ds: activeCell.ds, idx: next }); };

  const setDay = (ds, meal, v) => { setForm((f) => ({ ...f, days: { ...f.days, [ds]: { ...(f.days[ds] || {}), [meal]: v } } })); setDirty(true); };
  const toggleCust = (id) => { setForm((f) => ({ ...f, customer_ids: f.customer_ids.includes(id) ? f.customer_ids.filter((x) => x !== id) : [...f.customer_ids, id] })); setDirty(true); };
  const paste = (ds) => { if (clip) { setForm((f) => ({ ...f, days: { ...f.days, [ds]: { ...clip } } })); setDirty(true); } };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Menü adı gerekli"); return; }
    if (!/^\d{4}-\d{2}$/.test(form.month)) { toast.error("Ay seçin"); return; }
    setSaving(true);
    try {
      const body = { month: form.month, name: form.name, days: form.days, customer_ids: form.customer_ids, note: form.note || "" };
      const { data } = form.id ? await api.put(`/menus/${form.id}`, body) : await api.post("/menus", body);
      setForm({ ...form, id: data.id }); setDirty(false);
      toast.success("Menü kaydedildi");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const days = monthDays(form.month);
  const filled = days.filter((d) => MEALS.some(([k]) => form.days[d]?.[k]?.trim())).length;

  return (
    <div className="space-y-5 pb-24" data-testid="menu-editor">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div className="flex items-center gap-3">
          <button onClick={onClose} data-testid="menu-editor-back" className="h-10 w-10 rounded-lg border border-border flex items-center justify-center hover:bg-accent"><ArrowLeft className="h-4 w-4" /></button>
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">{form.id ? "Menüyü Düzenle" : "Yeni Menü"}</h1>
            <p className="text-sm text-muted-foreground mt-1">{filled}/{days.length} gün dolu · Her gün için 7 alan; alana tıklayıp kütüphaneden yemek seçin veya yazın</p>
          </div>
        </div>
        <div className="flex gap-2">
          {form.id && <a href={`${BACKEND}/api/menus/${form.id}/pdf`} target="_blank" rel="noreferrer" data-testid="menu-editor-pdf"><Button variant="outline" className="rounded-full h-11 gap-2"><FileDown className="h-4 w-4" /> PDF</Button></a>}
          {form.id && <Button variant="outline" onClick={() => setEmailOpen(true)} disabled={dirty} title={dirty ? "Önce kaydedin" : ""} data-testid="menu-editor-email" className="rounded-full h-11 gap-2"><Mail className="h-4 w-4" /> E-posta</Button>}
          <Button onClick={save} disabled={saving} data-testid="menu-editor-save" className="rounded-full h-11 px-6 font-semibold gap-2">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Kaydet</Button>
        </div>
      </div>

      <Card className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
        <F label="Menü Adı"><Input value={form.name} onChange={(e) => { setForm({ ...form, name: e.target.value }); setDirty(true); }} data-testid="menu-name" className="h-11" /></F>
        <F label="Ay"><input type="month" value={form.month} disabled={!!form.id} onChange={(e) => { setForm({ ...form, month: e.target.value }); setDirty(true); }} data-testid="menu-month" className="h-11 w-full rounded-md border border-input bg-background px-3 text-sm font-mono disabled:opacity-60" /></F>
        <F label="Not (opsiyonel)"><Input value={form.note || ""} onChange={(e) => { setForm({ ...form, note: e.target.value }); setDirty(true); }} data-testid="menu-note" className="h-11" placeholder="Örn: Ramazan menüsü" /></F>
        <div className="md:col-span-3">
          <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Atanan Müşteriler</Label>
          <div className="mt-2 flex flex-wrap gap-2" data-testid="menu-customers">
            {customers.length === 0 && <span className="text-sm text-muted-foreground">Önce müşteri ekleyin</span>}
            {customers.map((c) => { const on = form.customer_ids.includes(c.id); return (
              <label key={c.id} className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm cursor-pointer ${on ? "border-primary bg-primary/10" : "border-border"}`} data-testid={`menu-customer-${c.id}`}>
                <Checkbox checked={on} onCheckedChange={() => toggleCust(c.id)} /> {c.short_name || c.name}
              </label>
            ); })}
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-5 items-start">
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="menu-days-table">
            <thead><tr className="border-b border-border bg-muted/40 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <th className="px-3 py-3 w-28">Gün</th><th className="px-3 py-3" colSpan={7}>Öğle Yemeği Menüsü</th><th className="px-3 py-3 w-20"></th>
            </tr></thead>
            <tbody>
              {days.map((ds) => { const d = new Date(`${ds}T00:00:00`); const wk = d.getDay() === 0 || d.getDay() === 6; const slots = getSlots(ds); return (
                <tr key={ds} className={`border-b border-border/60 ${wk ? "bg-muted/20" : ""} ${activeCell?.ds === ds ? "bg-primary/5" : ""}`} data-testid={`menu-day-${ds}`}>
                  <td className="px-3 py-2 font-mono text-xs whitespace-nowrap"><span className="font-bold">{ds.slice(8)}</span> <span className={wk ? "text-primary" : "text-muted-foreground"}>{GUN[d.getDay()]}</span></td>
                  {slots.map((v, idx) => (
                    <td key={idx} className="px-1 py-1.5">
                      <input value={v} onChange={(e) => setSlot(ds, idx, e.target.value)} onFocus={() => setActiveCell({ ds, idx })} data-testid={`menu-slot-${ds}-${idx}`}
                        className={`h-9 w-full min-w-[110px] rounded-md border bg-background px-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary ${activeCell?.ds === ds && activeCell?.idx === idx ? "border-primary" : "border-input"}`} />
                    </td>
                  ))}
                  <td className="px-2 py-1.5">
                    <div className="flex gap-1">
                      <button onClick={() => { setClip({ ...(form.days[ds] || {}) }); toast.info(`${ds.slice(8)}. gün kopyalandı`); }} data-testid={`menu-copy-day-${ds}`} title="Günü kopyala" className="h-8 w-8 rounded-md hover:bg-accent inline-flex items-center justify-center text-muted-foreground"><Copy className="h-3.5 w-3.5" /></button>
                      <button onClick={() => paste(ds)} disabled={!clip} data-testid={`menu-paste-day-${ds}`} title="Yapıştır" className="h-8 w-8 rounded-md hover:bg-accent inline-flex items-center justify-center text-muted-foreground disabled:opacity-30"><ClipboardPaste className="h-3.5 w-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ); })}
            </tbody>
          </table>
        </div>
      </Card>
      <div className="xl:sticky xl:top-4"><DishLibrary onPick={pickDish} activeCell={activeCell} /></div>
      </div>

      {emailOpen && <MenuEmailDialog menu={{ ...form, customers: customers.filter((c) => form.customer_ids.includes(c.id)) }} onClose={() => setEmailOpen(false)} />}

      {dirty && <div className="fixed bottom-4 right-4 z-30"><Button onClick={save} disabled={saving} data-testid="menu-editor-save-floating" className="rounded-full h-12 px-6 shadow-xl gap-2">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Kaydet</Button></div>}
    </div>
  );
}

const F = ({ label, children }) => (
  <div className="space-y-2"><Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</Label>{children}</div>
);
